import React, { useState } from "react";
import { api } from "../lib/api";
import { Gem, Lock, Loader2, Check, Award, Shield } from "lucide-react";
import { toast } from "sonner";

/**
 * EliteUpgrade — Provider Dashboard card driving Level 3 of the trust stack.
 *
 * Prerequisites:
 *   - Identity Verified (Stripe Identity stub)
 *   - TREL Certified (5-page training)
 *
 * Flow:
 *   1) /providers/elite/request          (creates payment intent — STUBBED Stripe)
 *   2) /providers/elite/pay              ($60 — STUBBED success)
 *   3) /providers/elite/start-screening  (Checkr invitation — STUBBED)
 *   4) /providers/elite/checkr-webhook   (Checkr completes — STUBBED success)
 *
 * NO SSN / ID images / background-report content is ever stored on TREL.
 */
export default function EliteUpgrade({ provider, onChange }) {
  const identityOk = !!provider?.identity_verified;
  const certOk = !!provider?.trel_certified || !!(provider?.certification && provider.certification.completed_at);
  const elite = !!provider?.elite_badge_active;
  const paid = provider?.elite_payment_status === "Paid";
  const bgStatus = provider?.background_status || "Not Started";

  const [busy, setBusy] = useState(false);

  const prereqsOk = identityOk && certOk;

  // Pre-computed labels keep JSX clean (no nested ternaries)
  const screeningLabel = (() => {
    if (bgStatus === "Approved") return "Background screening approved";
    if (bgStatus === "Pending") return "Background screening pending";
    if (bgStatus === "Needs Attention") return "Screening needs attention";
    return "Background screening";
  })();

  const buttonLabel = (() => {
    if (busy) return "Processing…";
    if (prereqsOk) return "Upgrade to Elite — $60";
    return "Complete prerequisites first";
  })();

  const ButtonIcon = (() => {
    if (busy) return <Loader2 size={14} className="animate-spin" />;
    if (prereqsOk) return <Gem size={14} />;
    return <Lock size={14} />;
  })();

  const upgrade = async () => {
    if (!prereqsOk) {
      toast.error("Complete Identity Verification and TREL Certification first.");
      return;
    }
    setBusy(true);
    try {
      if (!paid) {
        await api.post("/providers/elite/request");
        await api.post("/providers/elite/pay");
        toast.success("$60 screening fee received");
      }
      if (bgStatus === "Not Started" || bgStatus === "Needs Attention") {
        await api.post("/providers/elite/start-screening");
        toast.success("Background screening started");
      }
      // STUB: simulate Checkr completion. In production this is a signed webhook.
      await api.post("/providers/elite/checkr-webhook", { result: "approved" });
      toast.success("TREL Elite badge unlocked");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Elite upgrade failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-3xl overflow-hidden border border-[#C9A45C]/40 shadow-luxury-lg"
      data-testid="elite-upgrade-card"
    >
      <div className="grid md:grid-cols-5">
        <div className={`md:col-span-3 p-7 ${elite ? "bg-gradient-to-br from-[#C9A45C] via-[#D4B16E] to-[#E0BE7A] text-[#071A2D]" : "bg-[#FFF8EE]"}`}>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold">
            Trust · Level 3 · Optional Premium
          </div>
          <h3 className="font-serif text-3xl mt-2 flex items-center gap-2">
            <Gem size={22} /> TREL Elite Concierge
          </h3>
          <p className="text-sm mt-3 max-w-md leading-relaxed opacity-90">
            The highest tier of trust on TREL. Includes enhanced background
            screening through our partner Checkr. Customers booking corporate,
            luxury, and VIP experiences look for the Elite gold badge.
          </p>
          <ul className="mt-5 space-y-2 text-sm">
            <li className="flex items-start gap-2"><Shield size={14} className="mt-1"/> Identity Verified prerequisite</li>
            <li className="flex items-start gap-2"><Award size={14} className="mt-1"/> TREL Certified prerequisite</li>
            <li className="flex items-start gap-2"><Gem size={14} className="mt-1"/> Enhanced screening (Checkr — TREL stores no PII)</li>
            <li className="flex items-start gap-2"><Check size={14} className="mt-1"/> Gold Elite badge on every Concierge offering &amp; profile</li>
          </ul>
        </div>

        <div className="md:col-span-2 p-7 bg-[#071A2D] text-[#FFF8EE] flex flex-col">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">
            Screening Fee
          </div>
          <div className="font-serif text-5xl mt-2">$60</div>
          <div className="text-xs opacity-70 mt-1">One-time · Non-refundable</div>

          <div className="mt-5 space-y-2 text-xs">
            <Step done={identityOk} label="Identity Verified" />
            <Step done={certOk} label="TREL Certified" />
            <Step
              done={paid}
              label={paid ? "$60 fee paid" : "Pay $60 screening fee"}
            />
            <Step
              done={bgStatus === "Approved"}
              warn={bgStatus === "Needs Attention"}
              label={screeningLabel}
            />
          </div>

          <div className="mt-auto pt-6">
            {elite ? (
              <div className="rounded-xl bg-[#C9A45C] text-[#071A2D] px-4 py-3 text-center font-semibold text-sm flex items-center justify-center gap-2">
                <Gem size={14} /> Elite badge active
              </div>
            ) : (
              <button
                onClick={upgrade}
                disabled={busy || !prereqsOk}
                data-testid="elite-upgrade-btn"
                className="w-full rounded-full bg-[#C9A45C] text-[#071A2D] py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition"
              >
                {ButtonIcon}
                {buttonLabel}
              </button>
            )}
            <div className="text-[10px] opacity-60 mt-3 leading-relaxed">
              TREL never stores SSN, ID images, or background-report content. Only screening status (Approved / Pending / Needs Attention).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ done, warn, label }) {
  let color = "text-[#FFF8EE]/55";
  let icon = "○";
  if (done) {
    color = "text-[#C9A45C]";
    icon = "✓";
  } else if (warn) {
    color = "text-[#FF7A45]";
    icon = "!";
  }
  return (
    <div className={`flex items-center gap-2 ${color}`}>
      <span className="w-4 inline-block text-center">{icon}</span>
      {label}
    </div>
  );
}
